import StickyTableResponsive from '../../../components/common/StickyTableResponsive';
import SortableTh from '../../../components/common/SortableTh';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTableSort } from '../../../hooks/useTableSort';
import { Link } from 'react-router-dom';
import MasterLayout from '../../../masterLayout/MasterLayout';
import Breadcrumb from '../../../components/Breadcrumb';
import {
  AppAlert,
  AppButton,
  AppSelect,
  AppSpinner,
} from '../../../components/ui';
import KkpColorBadge from '../../../components/kkp/KkpColorBadge';
import secteursAdminService from '../../../services/secteursAdminService';
import secteursService from '../../../services/secteursService';
import { formatDashboardCa, formatDashboardNumber } from '../../../utils/dashboardV2Utils';
import { buildCommercialColorByName, getCommercialColor } from '../../../utils/commercialBadgeUtils';
import { ROUTES } from '../../../config/routes';
import { SOCIETE_BRAND } from '../../../config/societeBrand';
import { notify } from '../../../components/common/AppToast';

const SOCIETE_CONFIG = SOCIETE_BRAND;

const VENDEUR_SORT_COLUMNS = {
  vendeur: { type: 'string', getValue: (row) => row.nom },
  ca: { type: 'number', getValue: (row) => row.ca ?? 0 },
};

const PRODUIT_DETAIL_SORT_COLUMNS = {
  produit: { type: 'string', getValue: (row) => row.label },
  famille: { type: 'string', getValue: (row) => row.famille },
  ca: { type: 'number', getValue: (row) => row.total_ca ?? 0 },
  vendeurs: { type: 'string', getValue: (row) => (row.vendeurs || []).map((v) => v.nom).join(', ') },
};

const SocieteSection = ({ societeKey, data, colorByName }) => {
  const vendeurs = data?.vendeurs ?? [];
  const { sortedRows, sort, toggleSort, getSortIcon } = useTableSort(vendeurs, VENDEUR_SORT_COLUMNS);

  if (!data) return null;

  const totalCa = data.total_ca ?? vendeurs.reduce((s, v) => s + (v.ca || 0), 0);
  const config = SOCIETE_CONFIG[societeKey];

  if (totalCa <= 0) return null;

  return (
    <div className="card mb-24">
      <div className={`card-header ${config.headerClass}`}>
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">{config.label}</h6>
          <span className="badge bg-light text-dark">{formatDashboardCa(totalCa)}</span>
        </div>
      </div>
      <div className="card-body">
        <StickyTableResponsive>
          <table className="table table-sm">
            <thead>
              <tr>
                <SortableTh sortKey="vendeur" label="Vendeur" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="ca" label="CA" className="text-end" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((v, i) => (
                <tr key={i}>
                  <td>
                    <KkpColorBadge color={getCommercialColor(colorByName, v.nom)} vivid>
                      {v.nom}
                    </KkpColorBadge>
                  </td>
                  <td className="text-end">{formatDashboardCa(v.ca)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </StickyTableResponsive>
        <small className="text-secondary-light">
          Quantité totale : {formatDashboardNumber(data.total_qty ?? 0)}
          {data.total_nb_factures != null && ` · ${data.total_nb_factures} factures/commandes`}
        </small>
      </div>
    </div>
  );
};

const ProduitDetailTable = ({ produits, colorByName }) => {
  const { sortedRows, sort, toggleSort, getSortIcon } = useTableSort(produits, PRODUIT_DETAIL_SORT_COLUMNS);

  return (
    <StickyTableResponsive>
      <table className="table table-hover mb-0">
        <thead>
          <tr>
            <SortableTh sortKey="produit" label="Produit" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
            <SortableTh sortKey="famille" label="Famille" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
            <SortableTh sortKey="ca" label="CA" className="text-end" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
            <SortableTh sortKey="vendeurs" label="Vendeurs" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((p, i) => (
            <tr key={i}>
              <td><strong>{p.label}</strong>{p.ref && <small className="d-block text-muted">{p.ref}</small>}</td>
              <td>{p.famille || '—'}</td>
              <td className="text-end">{formatDashboardCa(p.total_ca)}</td>
              <td>
                {(p.vendeurs || []).map((v, j) => (
                  <KkpColorBadge
                    key={j}
                    color={getCommercialColor(colorByName, v.nom)}
                    vivid
                    className="badge me-1"
                  >
                    {v.nom}: {formatDashboardCa(v.ca)}
                  </KkpColorBadge>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </StickyTableResponsive>
  );
};

const SecteursAdminProduitsCommerciauxPage = () => {
  const currentYear = new Date().getFullYear();
  const [annee, setAnnee] = useState(currentYear);
  const [produits, setProduits] = useState(null);
  const [produitsDetailles, setProduitsDetailles] = useState(null);
  const [commerciaux, setCommerciaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const colorByName = useMemo(() => buildCommercialColorByName(commerciaux), [commerciaux]);

  useEffect(() => {
    secteursService.getCommerciaux()
      .then(setCommerciaux)
      .catch(() => setCommerciaux([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await secteursAdminService.getProduitsCommerciaux(annee);
      setProduits(data.produits ?? {});
      setProduitsDetailles(data.produits_detailles ?? {});
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [annee]);

  useEffect(() => { load(); }, [load]);

  const hasData = produits && Object.values(produits).some((s) => (s?.total_ca ?? 0) > 0);

  return (
    <MasterLayout>
      <Breadcrumb
        title="Produits par société et commercial"
        subtitle={`Répartition CA par vendeur — année ${annee}`}
        actions={(
          <div className="d-flex gap-2 align-items-center">
            <AppButton as={Link} to={ROUTES.secteurs.admin} size="sm" variant="outline-secondary">Retour</AppButton>
            <AppButton as={Link} to={ROUTES.secteurs.adminProduitsSociete} size="sm" variant="outline-primary">Mappings</AppButton>
            <AppSelect size="sm" className="w-auto" value={annee} onChange={(e) => setAnnee(Number(e.target.value))}>
              {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </AppSelect>
          </div>
        )}
      />

      {loading ? (
        <AppSpinner />
      ) : !hasData ? (
        <AppAlert variant="warning">
          Aucune donnée pour {annee}. Vérifiez la synchronisation Dolibarr et les mappings produit → société.
        </AppAlert>
      ) : (
        <>
          {['koikispass', 'iticonseil', 'autre'].map((key) => (
            <SocieteSection key={key} societeKey={key} data={produits[key]} colorByName={colorByName} />
          ))}
          {produitsDetailles && Object.keys(SOCIETE_CONFIG).map((key) => {
            const detail = produitsDetailles[key];
            if (!detail?.produits?.length) return null;
            return (
              <div key={`detail-${key}`} className="card mb-24">
                <div className={`card-header ${SOCIETE_CONFIG[key].headerClass}`}>
                  <h6 className="mb-0">{SOCIETE_CONFIG[key].label} — détail par produit</h6>
                </div>
                <div className="card-body p-0">
                  <ProduitDetailTable produits={detail.produits} colorByName={colorByName} />
                </div>
              </div>
            );
          })}
        </>
      )}
    </MasterLayout>
  );
};

export default SecteursAdminProduitsCommerciauxPage;
